import React from 'react';
import { motion } from 'framer-motion';
import {
    Smartphone, Tablet, Cpu, Zap, Cloud, Globe,
    Shield, Layers, Activity, Layout, Search,
    Settings, Battery, Wifi, Play, ArrowRight, Code2, ChevronLeft, Palette
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO_Component from '../../components/SEO';

const AndroidDevelopment = () => {
    const features = [
        { title: "Native Development", desc: "Kotlin & Java for the ultimate Android experience.", icon: Cpu },
        { title: "Hybrid Solutions", desc: "React Native or Flutter for cross-platform apps.", icon: Smartphone },
        { title: "Material Design 3", desc: "Implementing latest design languages and UI.", icon: Layout },
        { title: "API Integration", desc: "Connecting with complex backend systems.", icon: Cloud },
        { title: "Optimization", desc: "Ensuring low battery and smooth performance.", icon: Battery },
        { title: "Store Management", desc: "Play Store submissions and ASO handling.", icon: Play }
    ];

    return (
        <div className="bg-[#02000c] min-h-screen text-white pt-20 overflow-hidden">
            <SEO_Component
                title="Android App Development | Custom Native & Hybrid Apps - MehareTech"
                description="We build high-performance Android applications using Kotlin, Java, Flutter, and React Native. Custom mobile solutions with Material Design and seamless API integrations."
                keywords="android app development, mobile application agency India, Kotlin developers, Flutter app development, cross platform mobile apps, React Native agency, play store publishing"
            />

            {/* Back to Services Link */}
            <div className="container mx-auto px-6 mb-4">
                <Link to="/services" className="inline-flex items-center gap-2 text-emerald-500 font-bold uppercase tracking-widest text-[10px] hover:text-white transition-colors group">
                    <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Services
                </Link>
            </div>

            {/* Hero Section */}
            <section className="relative py-12 px-6">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-emerald-600/10 blur-[120px] -z-10"></div>

                <div className="container mx-auto text-center max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-emerald-500 font-black uppercase tracking-[0.3em] text-[10px] mb-2 block">
                            Mobile Innovation
                        </span>
                        <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tighter">
                            ELITE <span className="bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 bg-clip-text text-transparent">ANDROID</span> SOLUTIONS
                        </h1>
                        <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto font-medium">
                            We build mobile applications that drive engagement and business growth.
                        </p>
                        <button className="px-8 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-all flex items-center gap-2 mx-auto text-xs">
                            Begin Journey <ArrowRight size={18} />
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* Grid */}
            <section className="py-12 px-6 bg-slate-900/40 border-y border-white/5">
                <div className="container mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        {features.map((item, i) => (
                            <motion.div
                                key={i}
                                className="p-6 bg-slate-900 border border-white/5 rounded-2xl hover:border-emerald-500/30 transition-all group"
                            >
                                <item.icon size={26} className="text-emerald-500 mb-4 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-bold mb-1">{item.title}</h3>
                                <p className="text-slate-400 text-[11px] leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Info Section */}
            <section className="py-12 px-6">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <div className="md:w-1/2">
                            <h2 className="text-3xl font-black mb-6 tracking-tight">
                                Peak <span className="text-emerald-500">Performance</span>
                            </h2>
                            <div className="space-y-3 font-medium">
                                {[
                                    { label: "Native Speed", icon: Wifi },
                                    { label: "Material You", icon: Palette },
                                    { label: "Secure Data", icon: Shield }
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                                        <div className="w-8 h-8 rounded-lg bg-emerald-600/10 flex items-center justify-center">
                                            <item.icon size={16} className="text-emerald-500" />
                                        </div>
                                        {item.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="md:w-1/2 relative group">
                            <img
                                src="https://images.pexels.com/photos/1092671/pexels-photo-1092671.jpeg"
                                alt="Mobile"
                                className="rounded-3xl shadow-xl w-full h-48 object-cover border border-white/10"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AndroidDevelopment;
